import { ValueFormatterParams } from 'ag-grid-community';

import { fileSizeFormatter } from '../../utils/fileSizeFormatter';
import { getFormattingLocales } from '../../utils/getFormattingLocales';

export const EMPTY_VALUE = '--';

export type FormatterOptions = {
  dateFormat?: string | null;
  displayFormat?: 'file_size_binary';
  digits?: number | null;
  disableThousandsSeparator?: boolean;
  factor?: number | null;
  locales?: string | string[];
  postfix?: string | null;
  falseLabel?: string | null;
  trueLabel?: string | null;
};

type FormatterFunction = <TValue>(
  value: TValue | null | undefined,
  options: FormatterOptions
) => TValue | string | null | undefined;

/** applies multiple formatters */
export const applyFormatters = <TValue>(
  formatters: FormatterFunction[],
  value: TValue | string | undefined | null,
  options: FormatterOptions
) => {
  return formatters.reduce((res, formatter) => {
    return formatter(res, options) ?? value;
  }, value);
};

export const combineFormatters =
  <TData, TValue>(formatters: FormatterFunction[], options: FormatterOptions) =>
  (params: ValueFormatterParams<TData, TValue>) => {
    if (formatters != null) {
      return applyFormatters(formatters, params.value, options);
    }
    return params.value;
  };

export const postfixFormatter = <TValue>(
  value: TValue | undefined | null,
  options: FormatterOptions
) => {
  if (options.postfix != null && value != null && value !== '') {
    return `${value}${options.postfix}`;
  }
  return value;
};

export const booleanFormatter = <TValue>(
  value: TValue | undefined | null,
  options: FormatterOptions
) => {
  if (typeof value !== 'boolean') {
    return value;
  }

  return value
    ? (options.trueLabel ?? 'true')
    : (options.falseLabel ?? 'false');
};

export const emptyValueFormatter = <TValue>(
  value: TValue | undefined | null
) => {
  return value === null || value === '' ? EMPTY_VALUE : value;
};

export const emptyArrayFormatter = <TValue>(
  value: TValue | undefined | null
) => {
  return Array.isArray(value) && value.length === 0 ? EMPTY_VALUE : value;
};

export const numberFormatter = <TValue>(
  value: TValue | null | undefined,
  options: FormatterOptions
) => {
  if (value == null || typeof value !== 'number') {
    return value;
  }

  const adjustedValue = numberFactorFormatter(value, options) ?? value;

  const formatterOptions: Intl.NumberFormatOptions = {
    useGrouping: true,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  };

  if (options.digits != null) {
    formatterOptions.maximumFractionDigits = options.digits;
    formatterOptions.minimumFractionDigits = options.digits;
  }

  // @tempfix: PF-4303 Should be replaced by `signDisplay: 'negative'` option for Intl.NumberFormat as soon as we support ES2023.
  const sanitizedValue =
    Math.round(
      adjustedValue * Math.pow(10, formatterOptions.maximumFractionDigits ?? 2)
    ) === 0
      ? 0
      : adjustedValue;

  if (options.disableThousandsSeparator) {
    formatterOptions.useGrouping = false;
  }

  const locales = options.locales ?? getFormattingLocales();

  if (options.displayFormat === 'file_size_binary') {
    return fileSizeFormatter(sanitizedValue, formatterOptions, locales);
  }

  return new Intl.NumberFormat(locales, formatterOptions).format(
    sanitizedValue
  );
};

export const numberFactorFormatter = <TValue>(
  value: TValue | null | undefined,
  options: FormatterOptions
) => {
  const { factor } = options;
  if (typeof value !== 'number' || typeof factor !== 'number') {
    return value;
  }
  return value * factor;
};

export const convertDateFormatStrToDateTimeFormatOption = (
  dateFormat: FormatterOptions['dateFormat']
) => {
  // default option
  let dateTimeFormatOption: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  switch (dateFormat) {
    case 'fullmonthAndYear':
      dateTimeFormatOption = {
        month: 'long',
        year: 'numeric',
      };
      break;
    case 'threelettermonthAndYear':
      dateTimeFormatOption = {
        month: 'short',
        year: 'numeric',
      };
      break;
    case 'shortmonthAndYear':
      dateTimeFormatOption = {
        month: '2-digit',
        year: 'numeric',
      };
      break;
    case 'yearOnly':
      dateTimeFormatOption = {
        year: 'numeric',
      };
      break;
    case 'fullMonthDayAndYear':
      dateTimeFormatOption = {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      };
      break;
    case 'fullMonthDayYearWithTime':
      dateTimeFormatOption = {
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        year: 'numeric',
      };
      break;
    case 'quarterAndYear':
      dateTimeFormatOption = {
        month: '2-digit',
        year: 'numeric',
      };
      break;
    default:
      break;
  }

  return dateTimeFormatOption;
};

export const dateFormatter = <TValue>(
  value: TValue | null | undefined,
  options: FormatterOptions
): string | TValue | null | undefined => {
  if (value == null || value === '' || typeof value !== 'string') {
    return value;
  }

  const dateTimeFormatOption = convertDateFormatStrToDateTimeFormatOption(
    options.dateFormat
  );

  const locales = options.locales ?? getFormattingLocales();

  const dateTimeFormat = new Intl.DateTimeFormat(locales, dateTimeFormatOption);

  const dateToFormat = new Date(value);

  if (Number.isNaN(dateToFormat.valueOf())) {
    // @tempfix: Some Inflexion data migration created wrong timestamps. This really shouldn't be here, but the calm the waves, it is ...
    if (value.toLowerCase().endsWith('zz')) {
      return dateFormatter(value.slice(0, -1), options);
    }

    return value;
  }

  // special formatting for 'quarterAndYear'
  if (options.dateFormat === 'quarterAndYear') {
    const yearAndMonthParts = dateTimeFormat.formatToParts(dateToFormat);
    const month = yearAndMonthParts.find((part) => part.type === 'month');
    const year = yearAndMonthParts.find((part) => part.type === 'year');
    if (!month || !year) {
      return '';
    }
    // calculate quarter
    const quarter = Math.ceil(Number.parseInt(month.value, 10) / 3);
    return `Q${quarter} ${year.value}`;
  }

  return dateTimeFormat.format(dateToFormat);
};

/** Shared display pipelines which should be used everywhere we show these data types. */
export const dateFormatters = [dateFormatter, emptyValueFormatter];
export const numberFormatters = [
  numberFormatter,
  postfixFormatter,
  emptyValueFormatter,
];
export const textFormatters = [
  postfixFormatter,
  booleanFormatter,
  emptyValueFormatter,
];
